import { useEffect, useState } from 'react'
import { CalendarPlus, ClipboardCheck } from 'lucide-react'
import { Badge } from '@/shared/components/Badge'
import {
  vistaDeDireccion,
  type EntrevistaDeDireccion,
} from '@/features/rh/api/vistaDeDireccion'
import {
  textoDeEntrevista,
  etiquetaDeResultado,
  esperaResultado,
  yaSeDecidio,
  oGuion,
} from '@/features/rh/lib/vistaDeDireccion'

const TONOS = {
  pendiente: 'amarillo',
  aprobado: 'verde',
  rechazado: 'rojo',
  cancelada: 'gris',
} as const

interface Props {
  candidateId: string
  /** Sin el permiso de decidir, la lista se ve pero no se opera. */
  puedeDecidir: boolean
  onAgendar: () => void
  onRegistrarResultado: (entrevista: EntrevistaDeDireccion) => void
  /** Sube cada vez que algo cambia afuera, para volver a pedir la lista. */
  recargaEn?: number
  /**
   * Para la ficha del candidato: solo muestra lo que ya hubo y no se pinta si
   * Dirección nunca lo entrevistó, en vez de ofrecer una sección vacía que
   * sugiere que falta algo por hacer.
   */
  soloConsulta?: boolean
}

/**
 * Las entrevistas de Dirección de un candidato (SRH-283).
 *
 * Se pueden agendar varias y agendar no mueve al candidato; lo que decide es
 * el resultado, y el primero que se registre es la decisión de Dirección (RH
 * confirma el cierre, SRH-311). Por eso, en
 * cuanto una tiene resultado, se deja de ofrecer agendar: otra entrevista ya
 * no podría cambiar nada y en pantalla daría a entender lo contrario.
 */
export const EntrevistasDeDireccion = ({
  candidateId,
  puedeDecidir,
  onAgendar,
  onRegistrarResultado,
  recargaEn = 0,
  soloConsulta = false,
}: Props) => {
  const [entrevistas, setEntrevistas] = useState<EntrevistaDeDireccion[]>([])
  const [cargando, setCargando] = useState(true)

  useEffect(() => {
    let vigente = true
    setCargando(true)
    vistaDeDireccion
      .entrevistasDe(candidateId)
      .then((lista) => vigente && setEntrevistas(lista))
      .catch(() => vigente && setEntrevistas([]))
      .finally(() => vigente && setCargando(false))
    return () => {
      vigente = false
    }
  }, [candidateId, recargaEn])

  const decidida = yaSeDecidio(entrevistas)

  if (soloConsulta && !cargando && entrevistas.length === 0) return null

  return (
    <section className="bg-white rounded-xl border border-[#E0E0E0]">
      <header className="flex items-center justify-between gap-3 px-6 py-4 border-b border-[#E0E0E0]">
        <h2 className="text-base font-semibold text-[#1E1E1F]">
          Entrevistas de Dirección
        </h2>
        {puedeDecidir && !decidida && !soloConsulta && (
          <button
            type="button"
            onClick={onAgendar}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-[#315FB9] hover:underline"
          >
            <CalendarPlus className="w-4 h-4" />
            Agendar
          </button>
        )}
      </header>

      <div className="p-6">
        {cargando ? (
          <p className="text-sm text-gray-500">Cargando entrevistas…</p>
        ) : entrevistas.length === 0 ? (
          <p className="text-sm text-gray-500">
            Sin entrevistas agendadas. Dirección puede decidir directamente o
            agendar una.
          </p>
        ) : (
          <ul className="space-y-3">
            {entrevistas.map((e) => {
              const resultado = etiquetaDeResultado(e)
              return (
                <li
                  key={e.id}
                  className="flex flex-wrap items-start justify-between gap-3 rounded-lg border border-[#E0E0E0] p-4"
                >
                  <div className="min-w-0">
                    <p className="text-sm text-[#1B2128]">{textoDeEntrevista(e)}</p>
                    {e.locationOrLink && (
                      <p className="text-xs text-gray-500 mt-1 break-words">
                        {e.locationOrLink}
                      </p>
                    )}
                    {e.result !== null && (
                      <p className="text-xs text-gray-500 mt-1">
                        {e.result === 'rejected' && `Motivo: ${oGuion(e.reason)}`}
                        {e.comments && ` · ${e.comments}`}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center gap-3">
                    <Badge color={TONOS[resultado.tono]}>{resultado.texto}</Badge>
                    {puedeDecidir && !soloConsulta && esperaResultado(e) && !decidida && (
                      <button
                        type="button"
                        onClick={() => onRegistrarResultado(e)}
                        className="inline-flex items-center gap-1.5 text-sm font-medium text-[#315FB9] hover:underline whitespace-nowrap"
                      >
                        <ClipboardCheck className="w-4 h-4" />
                        Registrar resultado
                      </button>
                    )}
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </section>
  )
}
